import React, { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';
import { EntityType, AttachmentContext } from '@constants/attachments';
import './AttachmentUploader.scss';

const AttachmentUploader = ({
  entityType,
  entityId,
  context = AttachmentContext.GENERAL,
  onUploadSuccess,
  multiple = false,
  accept = "image/*,application/pdf",
  className = "",
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  disabled = false
}) => {
  const { t } = useLanguage();
  const notify = useNotification();
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file) => {
    if (file.size > maxFileSize) {
      notify.error(t('attachments.fileTooLarge', { size: Math.round(maxFileSize / 1024 / 1024) }));
      return false;
    }
    return true;
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const failedUploads = [];
    const successfulUploads = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!validateFile(file)) {
        failedUploads.push(file.name);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId.toString());
      formData.append('attachment_context', context);
      formData.append('display_order', i.toString());

      // Debug: Log what we're sending
      console.log('FormData contents:', {
        file: file.name,
        entity_type: entityType,
        entity_id: entityId.toString(),
        attachment_context: context,
        display_order: i.toString()
      });

      try {
        const response = await api.attachments.uploadAndAttach(formData);

        successfulUploads.push(response.data);
        onUploadSuccess?.(response.data);
      } catch (error) {
        console.error('Upload failed:', error);
        failedUploads.push(file.name);
      }
    }

    setIsUploading(false);

    // Show success/error notifications
    if (successfulUploads.length > 0) {
      notify.success(t('attachments.uploadSuccess', { count: successfulUploads.length }));
    }
    if (failedUploads.length > 0) {
      notify.error(t('attachments.uploadFailed', { files: failedUploads.join(', ') }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    if (!multiple && files.length > 1) {
      notify.warning(t('attachments.singleFileOnly'));
      return;
    }

    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
    // Reset input value to allow same file upload again
    e.target.value = '';
  };

  return (
    <div
      className={`attachment-uploader ${className} ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        type="file"
        id={`file-input-${entityType}-${entityId}`}
        multiple={multiple}
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        style={{ display: 'none' }}
      />

      <label
        htmlFor={`file-input-${entityType}-${entityId}`}
        className="upload-area"
      >
        {isUploading ? (
          <div className="upload-status">
            <div className="loading-spinner"></div>
            <span>{t('attachments.uploading')}</span>
          </div>
        ) : (
          <div className="upload-content">
            <div className="upload-icon">📁</div>
            <div className="upload-text">
              <span className="primary-text">
                {t('attachments.clickToUpload')}
                {multiple && t('attachments.orDragMultiple')}
                {!multiple && t('attachments.orDragSingle')}
              </span>
              <span className="secondary-text">
                {accept === 'image/*' ?
                  t('attachments.imagesOnly') :
                  t('attachments.supportedFormats')
                }
              </span>
            </div>
          </div>
        )}
      </label>
    </div>
  );
};

export default AttachmentUploader;
